// Mind — scoring (M2, plan §2). Pure + deterministic.
//
// Mind is MONOTONIC: every item is written so agreeing = more of the capacity,
// so the headline is simply the mean (Strength). This is the key difference from
// its structural twin, Energetic Alignment.
//
// ⚠️ Answers are **1–5**, not 1–7. `AssessmentQuestionRenderer.renderAgreeDisagree`
// hardcodes a five-point scale and ignores each question's seeded `options`, so
// 1–5 is what the user can actually submit for this question type (see
// `src/data/mindAssessment.ts`). Every threshold below is calibrated to that
// range — recalibrate them together, or `Sharp` becomes unreachable.
//
// ⚠️ **Do NOT port Energetic's `Alignment = centeredness × cohesion` here.** That
// model multiplies by cohesion because a domain averaging its ideal out of a
// [low,low,high,high] split is hidden conflict, not balance. For a CAPACITY the
// same maths is simply wrong: [5,5,5,5,5,5,1] (strong, one weak link) is
// genuinely better than [3,3,3,3,3,3,3] (mediocre but even), yet multiplying
// would rank the second higher. So:
//   • Strength (the mean) is the score and the radar axis.
//   • Evenness is a DIAGNOSTIC, never a penalty — it exists to surface the weak
//     link (`weakest`), which is the actionable parameter.

// First-pass thresholds (tunable) — all calibrated to the renderer's 1–5 scale.
export const MIND_THRESHOLDS = {
  MIN: 1,
  MAX: 5,
  SD_MAX: 2, // max sample SD for a 1–5 split ≈ 2 (half the range)
  // Strength bands — five even slices of the 1–5 scale (0.8 wide).
  BAND_EMERGING: 1.8,
  BAND_STEADY: 2.6,
  BAND_STRONG: 3.4,
  BAND_SHARP: 4.2,
  // Evenness bands (higher evenness = more uniform across the 7 parameters).
  EVEN_HIGH: 67, // ≥ → Even
  EVEN_LOW: 33, // ≤ → Uneven
} as const;

/** Where a capacity sits. Reads naturally for every subsystem — "Metacognition
 *  is Emerging", "Decision Quality is Sharp". */
export type MindBand = 'Depleted' | 'Emerging' | 'Steady' | 'Strong' | 'Sharp';

/** How uniform a subsystem is across its 7 parameters. Diagnostic only. */
export type EvennessBand = 'Even' | 'Mixed' | 'Uneven';

/** One answered item, carried so the results view can list the parameters. */
export interface MindParamScore {
  parameter: string;
  answer: number; // 1–5
  headroom: number; // 5 − answer (how much room is left; the growth edge)
}

export interface SubsystemScore {
  subsystemId: string;
  strength: number; // 1–5 — the mean. THE score.
  level: number; // 0–10, strength normalized (the radar axis)
  min: number;
  max: number;
  range: number; // max − min
  sd: number; // sample SD
  evenness: number; // 0–1
  evennessPct: number; // 0–100 — higher = more uniform
  band: MindBand;
  evennessBand: EvennessBand;
  /** The lowest-scoring parameter — the weak link, and the thing to work on. */
  weakest: MindParamScore | null;
  /** The highest — the strength to lean on. */
  strongest: MindParamScore | null;
  params: MindParamScore[]; // the 7 answered items
}

export interface MindOverall {
  overallStrength: number; // 1–5, mean of the subsystem strengths
  overallLevel: number; // 0–10
  overallBand: MindBand;
  /** Mean of the within-subsystem evenness — how uniform each subsystem is. */
  overallEvenness: number; // 0–100
  /** Evenness ACROSS the subsystems — is the whole mind uniform, or spiky? */
  systemEvennessPct: number; // 0–100
  systemEvennessBand: EvennessBand;
  weakestSubsystemId: string | null;
  strongestSubsystemId: string | null;
}

export interface MindMap {
  subsystems: SubsystemScore[];
  overall: MindOverall;
}

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));
const round1 = (n: number) => Math.round(n * 10) / 10;
const round2 = (n: number) => Math.round(n * 100) / 100;

function mean(xs: number[]): number {
  return xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : 0;
}

/** Sample standard deviation (n−1). */
function sampleSd(xs: number[]): number {
  if (xs.length < 2) return 0;
  const m = mean(xs);
  const variance = xs.reduce((s, x) => s + (x - m) ** 2, 0) / (xs.length - 1);
  return Math.sqrt(variance);
}

/** 1–5 strength → the 0–10 radar level. */
export function levelFor(strength: number): number {
  const { MIN, MAX } = MIND_THRESHOLDS;
  return round1(clamp((strength - MIN) / (MAX - MIN), 0, 1) * 10);
}

export function bandFor(strength: number): MindBand {
  const t = MIND_THRESHOLDS;
  if (strength < t.BAND_EMERGING) return 'Depleted';
  if (strength < t.BAND_STEADY) return 'Emerging';
  if (strength < t.BAND_STRONG) return 'Steady';
  if (strength < t.BAND_SHARP) return 'Strong';
  return 'Sharp';
}

export function evennessBandFor(evennessPct: number): EvennessBand {
  const t = MIND_THRESHOLDS;
  if (evennessPct >= t.EVEN_HIGH) return 'Even';
  if (evennessPct <= t.EVEN_LOW) return 'Uneven';
  return 'Mixed';
}

/** Score one subsystem from its 7 ratings (each 1–5). `parameters` (parallel to
 *  `ratings`) is optional and only labels the per-parameter list. */
export function scoreSubsystem(subsystemId: string, ratings: number[], parameters?: string[]): SubsystemScore {
  const { MAX, SD_MAX } = MIND_THRESHOLDS;
  const strength = mean(ratings);
  const min = ratings.length ? Math.min(...ratings) : 0;
  const max = ratings.length ? Math.max(...ratings) : 0;
  const sd = sampleSd(ratings);
  const evenness = clamp(1 - sd / SD_MAX, 0, 1);
  const evennessPct = Math.round(evenness * 100);

  const params: MindParamScore[] = ratings.map((answer, i) => ({
    parameter: parameters?.[i] ?? `Parameter ${i + 1}`,
    answer,
    headroom: MAX - answer,
  }));

  // Ties resolve to the FIRST in registry order — deterministic, so a re-render
  // or a re-read of the same attempt never names a different weak link.
  let weakest: MindParamScore | null = null;
  let strongest: MindParamScore | null = null;
  for (const p of params) {
    if (!weakest || p.answer < weakest.answer) weakest = p;
    if (!strongest || p.answer > strongest.answer) strongest = p;
  }

  return {
    subsystemId,
    strength: round2(strength),
    level: levelFor(strength),
    min,
    max,
    range: max - min,
    sd: round2(sd),
    evenness: round2(evenness),
    evennessPct,
    band: bandFor(strength),
    evennessBand: evennessBandFor(evennessPct),
    weakest,
    strongest,
    params,
  };
}

/** Build the overall read from the per-subsystem scores. */
export function buildOverall(subsystems: SubsystemScore[]): MindOverall {
  const { SD_MAX } = MIND_THRESHOLDS;
  const strengths = subsystems.map(s => s.strength);
  const overallStrength = mean(strengths);
  const overallEvenness = Math.round(mean(subsystems.map(s => s.evennessPct)));

  // System evenness = spread ACROSS the 7 subsystems. Low = a spiky mind (sharp
  // in some faculties, depleted in others) even if each subsystem is internally
  // uniform. Diagnostic, like the per-subsystem evenness — never a penalty.
  const systemSd = sampleSd(strengths);
  const systemEvennessPct = Math.round(clamp(1 - systemSd / SD_MAX, 0, 1) * 100);

  let weakestSubsystemId: string | null = null;
  let strongestSubsystemId: string | null = null;
  let lo = Infinity;
  let hi = -Infinity;
  for (const s of subsystems) {
    if (s.strength < lo) { lo = s.strength; weakestSubsystemId = s.subsystemId; }
    if (s.strength > hi) { hi = s.strength; strongestSubsystemId = s.subsystemId; }
  }

  return {
    overallStrength: round2(overallStrength),
    overallLevel: levelFor(overallStrength),
    overallBand: bandFor(overallStrength),
    overallEvenness,
    systemEvennessPct,
    systemEvennessBand: evennessBandFor(systemEvennessPct),
    weakestSubsystemId,
    strongestSubsystemId,
  };
}

/** Build the full map from a set of per-subsystem scores (one or all 7). */
export function buildMindMap(subsystems: SubsystemScore[]): MindMap {
  return { subsystems, overall: buildOverall(subsystems) };
}

/** A single answered item (from the quiz). */
export interface MindCell {
  subsystemId: string;
  parameter: string;
  answer: number; // 1–5
}

/** Group answered cells by subsystem → per-subsystem scores → full map. */
export function buildMindResult(cells: MindCell[]): MindMap {
  const bySubsystem = new Map<string, MindCell[]>();
  for (const c of cells) {
    if (!bySubsystem.has(c.subsystemId)) bySubsystem.set(c.subsystemId, []);
    bySubsystem.get(c.subsystemId)!.push(c);
  }
  const subsystems = Array.from(bySubsystem.entries()).map(([id, list]) =>
    scoreSubsystem(id, list.map(c => c.answer), list.map(c => c.parameter)),
  );
  return buildMindMap(subsystems);
}

// ── Modular overview merge (latest take of each subsystem → full map) ────────
// Mind is modular: a user can take one subsystem's 7 items on their own
// (AssessmentQuiz filters by `dimension`). The overview stitches each
// subsystem's most recent attempt into one map.

export interface SubsystemMeta {
  completedAt: string;
  band: MindBand;
  evennessBand: EvennessBand;
  strength: number;
  level: number;
}

export interface MindOverview {
  result: MindMap | null;
  perSubsystem: Record<string, SubsystemMeta>;
}

export function mergeLatestSubsystemResults(
  attempts: Array<{ completedAt: string; result: MindMap | null | undefined }>,
  subsystemOrder: string[],
): MindOverview {
  const sorted = attempts
    .filter(a => a.result && a.completedAt)
    .slice()
    .sort((a, b) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime());

  const subsystems: SubsystemScore[] = [];
  const perSubsystem: Record<string, SubsystemMeta> = {};

  for (const id of subsystemOrder) {
    const found = sorted.find(a => a.result!.subsystems.some(s => s.subsystemId === id));
    if (!found) continue;
    const ss = found.result!.subsystems.find(s => s.subsystemId === id)!;
    perSubsystem[id] = {
      completedAt: found.completedAt,
      band: ss.band,
      evennessBand: ss.evennessBand,
      strength: ss.strength,
      level: ss.level,
    };
    subsystems.push(ss);
  }

  return { result: subsystems.length ? buildMindMap(subsystems) : null, perSubsystem };
}
